import { getMyanmarTime } from '../helper/helper';
import { POPUP_RULES, POPUP_RULE_BY_ID, POPUP_ID } from './popupRules';

// POPUP_ID được sinh từ popupRules.js — re-export để các chỗ gọi sẵn có
// (POPUP_ID.PU2, POPUP_ID.PU3, ...) không phải đổi import.
export { POPUP_ID };

// ═══════════════════════════════════════════════════════════════════════════
// Quy tắc chung — sheet BA "Phân nhóm và tối ưu luồng người dùng", mục 1
// (src/Mytel - ShweDeal - SHWEDEAL 2.0 UPGRADE.xlsx):
//
//   1. Một lần vào ShweDeal: bắt đầu khi mở webview từ MyID, hoặc quay lại từ
//      background sau ≥ 1 giờ. Chuyển trang, về Home, login/logout, reload,
//      quay lại từ background dưới 1 giờ đều KHÔNG tính là lần vào mới.
//      → trạng thái lần vào lưu ở sessionStorage (còn nguyên khi reload, mất khi
//        đóng webview) + mốc vào background (pm_hidden_at).
//   2. Tối đa 1 popup mỗi lần vào, tối đa 3 popup mỗi ngày. Hai popup cách nhau
//      tối thiểu 1 giờ, tính từ lúc popup THỰC SỰ HIỆN.
//   3. Popup bị bỏ qua (priority, cooldown, đủ 3 popup/ngày) không tính là đã hiện.
//   4. Chỉ hiện ở Home, ở lần đầu user về Home trong lần vào đó (xem
//      isHomeChecked / markHomeChecked — ModalRender dùng).
//   5. Guest: tần suất, giới hạn và cooldown đếm theo thiết bị. Login giữa chừng
//      thì giữ nguyên số popup đã hiện và cooldown; về Home thì xét popup của
//      user đã login nếu lần vào đó chưa hiện popup nào.
//
// Cooldown và giới hạn 3 popup/ngày lưu ở localStorage, dùng chung cả máy cho
// guest lẫn user — nhờ vậy login giữa chừng tự "giữ nguyên" như spec yêu cầu.
//
// Danh sách popup, thứ tự ưu tiên và tần suất riêng của từng popup khai báo tập
// trung ở popupRules.js — file này chỉ đọc, không hardcode popup nào.
// ═══════════════════════════════════════════════════════════════════════════

// sessionStorage
const KEY_VISIT = 'pm_visit';
const KEY_HIDDEN_AT = 'pm_hidden_at';
// localStorage
const KEY_LAST_SHOWN = 'pm_last_shown_ts';
const KEY_DAILY_COUNT = 'pm_daily_count';

const NEW_VISIT_AFTER_BACKGROUND_MS = 60 * 60 * 1000; // ≥ 1 giờ ở background = lần vào mới
const COOLDOWN_MS = 60 * 60 * 1000;                   // 1 giờ kể từ lần popup gần nhất HIỆN
const MAX_POPUPS_PER_DAY = 3;
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;          // cửa sổ 7 ngày cho popup weekly

// ─── helpers ─────────────────────────────────────────────────────────────────

// Ngày nghiệp vụ tính theo giờ Myanmar (UTC+6:30), KHÔNG dùng giờ máy/UTC.
function todayDateStr() {
  return getMyanmarTime().format('YYYY-MM-DD');
}

function readJSON(storage, key) {
  try { return JSON.parse(storage.getItem(key)); } catch (_) { return null; }
}

function writeJSON(storage, key, value) {
  storage.setItem(key, JSON.stringify(value));
}

// ─── lần vào ShweDeal (visit) ────────────────────────────────────────────────

function startNewVisit(now) {
  const visit = { startedAt: now, shown: false, homeChecked: [] };
  writeJSON(sessionStorage, KEY_VISIT, visit);
  sessionStorage.removeItem(KEY_HIDDEN_AT);
  return visit;
}

// Trả về lần vào hiện tại, tự mở lần vào mới khi:
//   - chưa có (vừa mở webview từ MyID — sessionStorage mới), hoặc
//   - trang từng vào background ≥ 1 giờ (kể cả khi webview bị OS reload lại).
// Reload bình thường: trang vừa "hidden" ngay trước khi tải lại nên vẫn cùng lần vào.
function currentVisit(now = Date.now()) {
  const visit = readJSON(sessionStorage, KEY_VISIT);
  const hiddenAt = parseInt(sessionStorage.getItem(KEY_HIDDEN_AT), 10);
  if (!visit || (hiddenAt && now - hiddenAt >= NEW_VISIT_AFTER_BACKGROUND_MS)) {
    return startNewVisit(now);
  }
  if (hiddenAt) sessionStorage.removeItem(KEY_HIDDEN_AT);
  return visit;
}

// Gọi từ listener `visibilitychange`. Trả về true khi vừa bắt đầu lần vào mới
// (quay lại từ background sau ≥ 1 giờ) để ModalRender xét popup lại từ đầu.
export function handleVisibilityChange(state, now = Date.now()) {
  if (state === 'hidden') {
    sessionStorage.setItem(KEY_HIDDEN_AT, String(now));
    return false;
  }
  const before = readJSON(sessionStorage, KEY_VISIT);
  const after = currentVisit(now);
  return !before || before.startedAt !== after.startedAt;
}

// audience: 'guest' hoặc `user:<id>`. Mỗi đối tượng được xét ở Home đúng 1 lần
// mỗi lần vào — guest login giữa chừng là đối tượng mới nên được xét lại (spec
// mục 5), còn giới hạn "1 popup / lần vào" vẫn chặn nếu đã có popup hiện.
export function isHomeChecked(audience) {
  return currentVisit().homeChecked.includes(audience);
}

export function markHomeChecked(audience) {
  const visit = currentVisit();
  if (!visit.homeChecked.includes(audience)) {
    visit.homeChecked.push(audience);
    writeJSON(sessionStorage, KEY_VISIT, visit);
  }
}

// ─── global popup gate ────────────────────────────────────────────────────────

function readDailyCount() {
  const record = readJSON(localStorage, KEY_DAILY_COUNT);
  return record && record.date === todayDateStr() ? record.count : 0;
}

// Lý do global gate đang chặn, null = không chặn.
//   'visit'       — lần vào này đã hiện 1 popup
//   'cooldown'    — chưa đủ 1 giờ kể từ lần popup gần nhất hiện
//   'daily_limit' — hôm nay đã hiện đủ 3 popup
export function getGateBlockReason(now = Date.now()) {
  if (currentVisit(now).shown) return 'visit';

  const lastShown = parseInt(localStorage.getItem(KEY_LAST_SHOWN), 10);
  if (lastShown && now - lastShown < COOLDOWN_MS) return 'cooldown';

  if (readDailyCount() >= MAX_POPUPS_PER_DAY) return 'daily_limit';

  return null;
}

// Returns false when the manager must block any popup.
// Returns true to let the individual popup's own conditions decide.
export function canShowPopup(now = Date.now()) {
  return getGateBlockReason(now) === null;
}

// Ghi nhận popup vừa HIỆN: chiếm suất của lần vào, bắt đầu cooldown, +1 vào
// số popup trong ngày. Trả về token để hoàn tác bằng undoPopupShown().
export function recordPopupShown(now = Date.now()) {
  const visit = currentVisit(now);
  visit.shown = true;
  writeJSON(sessionStorage, KEY_VISIT, visit);

  const token = {
    prevLastShown: localStorage.getItem(KEY_LAST_SHOWN),
    date: todayDateStr(),
  };
  localStorage.setItem(KEY_LAST_SHOWN, String(now));
  writeJSON(localStorage, KEY_DAILY_COUNT, { date: token.date, count: readDailyCount() + 1 });
  return token;
}

// Popup đã được chọn nhưng rốt cuộc không hiện gì (tự gọi skip) — spec mục 3:
// không tính là đã hiện. Trả lại suất lần vào, cooldown và số popup trong ngày.
export function undoPopupShown(token) {
  const visit = currentVisit();
  visit.shown = false;
  writeJSON(sessionStorage, KEY_VISIT, visit);

  if (!token) return;
  if (token.prevLastShown) localStorage.setItem(KEY_LAST_SHOWN, token.prevLastShown);
  else localStorage.removeItem(KEY_LAST_SHOWN);

  if (token.date === todayDateStr()) {
    writeJSON(localStorage, KEY_DAILY_COUNT, {
      date: token.date,
      count: Math.max(0, readDailyCount() - 1),
    });
  }
}

// ─── per-popup frequency tracking ────────────────────────────────────────────

// Popup khai báo scope: 'user' — hạn mức tần suất tính riêng từng tài khoản để
// dùng chung máy không đè nhau. scope: 'device' — dùng chung 1 key cho cả máy.
function freqKey(popupId, userId) {
  const rule = POPUP_RULE_BY_ID.get(popupId);
  return rule?.scope === 'user' && userId
    ? `pm_freq_${popupId}_${userId}`
    : `pm_freq_${popupId}`;
}

// Popup 'daily' lưu chuỗi ngày (so sánh theo ngày Myanmar), popup 'weekly' lưu
// timestamp (so sánh theo cửa sổ 7 ngày).
function isDailyFrequency(popupId) {
  return POPUP_RULE_BY_ID.get(popupId)?.frequency === 'daily';
}

// Returns true if the popup's own frequency allows it to show now.
// Truyền userId cho popup có scope 'user' để check theo từng tài khoản.
export function canShowSpecificPopup(popupId, userId) {
  const rule = POPUP_RULE_BY_ID.get(popupId);
  // FAIL-CLOSED: id không có trong popupRules.js thì không cho hiện. Trước đây
  // nhánh default trả true nên popup quên khai báo sẽ hiện lại mỗi page load.
  if (!rule) return false;
  if (rule.frequency === 'always') return true;

  const stored = localStorage.getItem(freqKey(popupId, userId));
  if (!stored) return true;

  return rule.frequency === 'daily'
    ? stored !== todayDateStr()
    : Date.now() - parseInt(stored, 10) >= ONE_WEEK_MS;
}

// Record that this specific popup was shown.
// Gọi NGAY khi popup được chọn hiển thị, không đợi user đóng — nếu đợi đóng thì
// user reload/thoát tab giữa chừng sẽ không ghi được gì và popup hiện lại mỗi
// page load (hạn mức "1 lần/ngày", "1 lần/tuần" mất tác dụng).
// Truyền userId cho popup có scope 'user'.
export function recordSpecificPopupShown(popupId, userId) {
  const rule = POPUP_RULE_BY_ID.get(popupId);
  if (!rule || rule.frequency === 'always') return;

  localStorage.setItem(
    freqKey(popupId, userId),
    isDailyFrequency(popupId) ? todayDateStr() : Date.now().toString(),
  );
}

// Hoàn tác việc ghi nhận ở trên. Dùng khi popup đã được chọn nhưng rốt cuộc
// KHÔNG hiển thị gì (popup tự fetch xong thấy rỗng và gọi skip) — theo spec thì
// trường hợp đó không được tính là một lần hiển thị.
export function clearSpecificPopupShown(popupId, userId) {
  localStorage.removeItem(freqKey(popupId, userId));
}

// ─── debug snapshot (call from DevTools or ModalRender logs) ─────────────────

export function debugState(userId) {
  const now = Date.now();
  const today = todayDateStr();
  const visit = currentVisit(now);
  const lastShown = parseInt(localStorage.getItem(KEY_LAST_SHOWN), 10);
  const cooldownMs = lastShown ? Math.max(0, COOLDOWN_MS - (now - lastShown)) : 0;
  const mm = String(Math.floor(cooldownMs / 60000)).padStart(2, '0');
  const ss = String(Math.floor((cooldownMs % 60000) / 1000)).padStart(2, '0');

  // Duyệt thẳng popupRules.js — thêm/gỡ popup là log tự cập nhật theo.
  const perPopup = {};
  for (const rule of [...POPUP_RULES].sort((a, b) => a.priority - b.priority)) {
    const stored = localStorage.getItem(freqKey(rule.id, userId));
    let lastShownPopup = 'never';
    if (stored) {
      lastShownPopup = isDailyFrequency(rule.id)
        ? `today=${stored === today} (${stored})`
        : `${Math.round((now - parseInt(stored, 10)) / 3600000)}h ago — ${new Date(parseInt(stored, 10)).toLocaleString()}`;
    }
    perPopup[rule.id.toUpperCase()] = {
      id: rule.id,
      priority: rule.priority,
      frequency: rule.frequency,
      scope: rule.scope,
      enabled: rule.enabled,
      canShow: canShowSpecificPopup(rule.id, userId),
      lastShown: lastShownPopup,
    };
  }

  return {
    visitStartedAt: new Date(visit.startedAt).toLocaleString(),
    shownThisVisit: visit.shown,
    homeChecked: visit.homeChecked,
    popupsToday: `${readDailyCount()} / ${MAX_POPUPS_PER_DAY}`,
    cooldownRemaining: cooldownMs > 0 ? `${mm}:${ss}` : 'none',
    gateBlockReason: getGateBlockReason(now),
    canShowGlobal: canShowPopup(now),
    perPopup,
  };
}
